import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AuthShell, AuthField, AuthError } from "@/components/AuthShell";

import { tr } from "@/lib/i18n";
const Submit = ({ busy, children, testid }) => (
  <Button type="submit" disabled={busy} className="w-full h-11 bg-[#0F52BA] hover:bg-[#0D47A1]" data-testid={testid}>
    {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{children}
  </Button>
);
const Back = () => <Link to="/accesso" className="block text-center text-sm text-[#0F52BA] hover:underline" data-testid="auth-back-login">{tr("Torna all'accesso")}</Link>;

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <AuthShell onSubmit={submit} testid="forgot-form" title={tr("Password dimenticata")}
      subtitle={tr("Inserisci l'email del tuo account: ti invieremo un link per sceglierne una nuova (valido 1 ora).")}>
      {sent ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex gap-3" data-testid="forgot-sent">
          <MailCheck className="w-5 h-5 shrink-0" />{tr("Se l'email corrisponde a un account attivo, riceverai a breve il link. Controlla anche la cartella spam.")}
        </div>
      ) : (
        <>
          <AuthField id="email" label={tr("Email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
          <AuthError>{err}</AuthError>
          <Submit busy={busy} testid="forgot-submit-btn">{tr("Invia link")}</Submit>
        </>
      )}
      <Back />
    </AuthShell>
  );
}

export function ResetPassword() {
  const token = useSearchParams()[0].get("token") || "";
  const [pw, setPw] = useState({ a: "", b: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.a !== pw.b) return setErr(tr("Le due password non coincidono"));
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw.a }); toast.success(tr("Password aggiornata: ora puoi accedere")); nav("/accesso"); }
    catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <AuthShell onSubmit={submit} testid="reset-form" title={tr("Nuova password")} subtitle={tr("Scegli una nuova password di almeno 8 caratteri.")}>
      <div className="space-y-4">
        <AuthField id="pw1" label={tr("Nuova password")} type="password" minLength={8} required value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-password-input" />
        <AuthField id="pw2" label={tr("Ripeti la password")} type="password" minLength={8} required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-password-confirm-input" />
      </div>
      <AuthError>{token ? err : tr("Link non valido: manca il codice di reimpostazione.")}</AuthError>
      <Submit busy={busy} testid="reset-submit-btn">{tr("Salva password")}</Submit>
      <Back />
    </AuthShell>
  );
}

export function AcceptInvite() {
  const token = useSearchParams()[0].get("token") || "";
  const { reload, user, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ name: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    api.get(`/auth/invite/${encodeURIComponent(token)}`).then(({ data }) => setInfo(data)).catch((ex) => { setInfo(false); setErr(apiError(ex)); });
  }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { if (user) await logout(); await api.post("/auth/accept-invite", { token, ...f }); await reload(); nav("/"); }
    catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  return (
    <AuthShell onSubmit={submit} testid="invite-form" title={info ? tr("Unisciti a {0}", [info.company]) : tr("Invito")}
      subtitle={info ? tr("{0} ti ha invitato come {1}. Completa il tuo profilo per entrare.", [info.invited_by, info.role === "admin" ? tr("amministratore") : tr("membro")]) : null}>
      {info === null && <div className="text-sm text-slate-400">{tr("Verifica dell'invito…")}</div>}
      {info && (
        <div className="space-y-4">
          <AuthField id="email" label={tr("Email")} value={info.email} disabled data-testid="invite-email" />
          <AuthField id="name" label={tr("Nome e cognome")} required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-input" />
          <AuthField id="password" label={tr("Password")} type="password" minLength={8} required placeholder={tr("Almeno 8 caratteri")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="invite-password-input" />
        </div>
      )}
      <AuthError>{err}</AuthError>
      {info && <Submit busy={busy} testid="invite-submit-btn">{tr("Crea account ed entra")}</Submit>}
      <Back />
    </AuthShell>
  );
}
